import {
  useDndContext,
  useDndMonitor,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { useEffect, useRef } from 'react';
import { delegateEscapeToDocument } from '../../../services/close-request-coordinator';

/**
 * Keep real Escape with the active or pending dnd-kit sensor until it releases
 * its listeners. Mount inside every DndContext; nested contexts own separate guards.
 * @returns No presentation; only interaction ownership and unmount cleanup.
 */
export function DragEscapeGuard() {
  const { draggableNodes } = useDndContext();
  const release = useRef<(() => void) | undefined>();
  const pendingObserver = useRef<MutationObserver | undefined>();
  const stopObserving = () => {
    pendingObserver.current?.disconnect();
    pendingObserver.current = undefined;
  };
  const clear = () => {
    stopObserving();
    release.current?.();
    release.current = undefined;
  };
  const acquire = (id: UniqueIdentifier, pending: boolean) => {
    if (release.current) {
      return;
    }
    const source = draggableNodes.get(id)?.node.current;
    release.current = delegateEscapeToDocument();
    // dnd-kit omits the abort monitor event if a pending source disappeared.
    if (pending && source) {
      pendingObserver.current = new MutationObserver(() => {
        if (!source.isConnected) {
          clear();
        }
      });
      pendingObserver.current.observe(source.ownerDocument, {
        childList: true,
        subtree: true,
      });
    }
  };
  useDndMonitor({
    onDragPending: ({ id }) => acquire(id, true),
    onDragStart: ({ active }) => {
      stopObserving();
      acquire(active.id, false);
    },
    onDragAbort: clear,
    onDragCancel: clear,
    onDragEnd: clear,
  });
  useEffect(
    () => () => {
      pendingObserver.current?.disconnect();
      release.current?.();
    },
    []
  );
  return null;
}
